import { getBackgroundPrompt, getNanoBananaCharacterPrompt } from './character-prompt-templates'
import {
  BACKGROUND_SCALE_MODES,
  type CharacterConfig,
  type CharacterPromptExample,
  getPrincipalCharacterConfig
} from './constants/character-registry'
import { ENRICHMENT_PROMPT_INPUT_MARKER, getEnrichmentPromptTemplateError } from './enrichment-prompts'
import type { Character, PlaygroundToolType } from './types'

// ============================================================================
// CHARACTER CATALOG - principal (code registry) + dynamic (per-stage DynamoDB)
// ============================================================================

export const CHARACTER_STATUSES = ['draft', 'active', 'retired'] as const
export type CharacterStatus = (typeof CHARACTER_STATUSES)[number]
export type CharacterSource = 'principal' | 'dynamic'

/**
 * Who can start new work with a character. `admin-only` hides it from non-admins; `hidden` is a
 * kill switch that removes it from pickers and the landing page for everyone. History always
 * keeps showing existing work. Stored per stage on the character settings row; absent = `all`.
 */
export const CHARACTER_VISIBILITIES = ['all', 'admin-only', 'hidden'] as const
export type CharacterVisibility = (typeof CHARACTER_VISIBILITIES)[number]
export const DEFAULT_CHARACTER_VISIBILITY: CharacterVisibility = 'all'

export function isCharacterVisibility(value: unknown): value is CharacterVisibility {
  return typeof value === 'string' && (CHARACTER_VISIBILITIES as readonly string[]).includes(value)
}

/** Dynamic characters only run through the serverless Gemini queue. */
export const DYNAMIC_CHARACTER_ALLOWED_TOOLS: PlaygroundToolType[] = ['character-nanobanana']

/** Producer-editable subset of CharacterConfig, stored on the character settings row. */
export interface DynamicCharacterDefinition {
  displayName: string
  fullName: string
  prompt: {
    characterIdentifier: string
    introDescription: string
    stylePrefix: string
    requiredFormat: string
    rules: string[]
    examples: CharacterPromptExample[]
  }
  background: CharacterConfig['background']
  inputPlaceholder: string
  landing: {
    title: string
    description: string
    hidden?: boolean
  }
  /**
   * Legacy single scale reference, read only as a fallback for rows saved before the
   * collection existed. Saves strip it and store the character's scale reference collection.
   */
  scaleReferenceKey?: string
  /** Utility-bucket key (`character-cards/<id>/…`) of the landing card artwork. */
  cardImageKey?: string
  /** Character the definition was duplicated from, for traceability only. */
  duplicatedFrom?: Character
}

/** Catalog entry returned by GET /characters. */
export interface CharacterSummary {
  id: Character
  displayName: string
  label: string
  fullName: string
  inputPlaceholder: string
  landing: DynamicCharacterDefinition['landing']
  allowedTools: PlaygroundToolType[]
  source: CharacterSource
  status: CharacterStatus
  visibility: CharacterVisibility
  /** Dynamic characters duplicated from another character, e.g. to reuse its landing artwork. */
  duplicatedFrom?: Character
  /** Utility-bucket key of an uploaded landing card image (dynamic characters only). */
  cardImageKey?: string
}

/**
 * A permanently deleted dynamic character. Only the label survives, so history
 * can keep naming the assets generated with it.
 */
export interface DeletedCharacterSummary {
  id: Character
  displayName: string
  deletedAt: string
}

export interface ListCharactersResponse {
  characters: CharacterSummary[]
  deletedCharacters: DeletedCharacterSummary[]
}

/** History filter value that selects every deleted character's assets. */
export const DELETED_CHARACTERS_FILTER = '__deleted__'

export function getDeletedCharacterLabel(displayName: string): string {
  return `${displayName} (deleted)`
}

export interface CreateCharacterRequest {
  scaleReferenceImageKeys?: string[]
  id: Character
  definition: DynamicCharacterDefinition
  status: Extract<CharacterStatus, 'draft' | 'active'>
  /** Defaults to `all`. Not copied when duplicating. */
  visibility?: CharacterVisibility
  /**
   * Copies saved Gemini settings, the guidelines URL, and effective reference images from this
   * character. Prompt overrides are not copied, because they name the source character.
   */
  duplicateFrom?: Character
}

/** Principals accept visibility and scale references; their definition and status live in code. */
export interface UpdateCharacterRequest {
  scaleReferenceImageKeys?: string[]
  definition?: DynamicCharacterDefinition
  status?: CharacterStatus
  visibility?: CharacterVisibility
}

export interface CharacterMutationResponse {
  scaleReferenceImageKeys?: string[]
  character: CharacterSummary
  definition: DynamicCharacterDefinition
  /** Present on duplicate creation; false means the saved settings need manual review. */
  copiedSettings?: boolean
  /** Number of source references successfully copied during duplicate creation. */
  copiedReferenceImages?: number
  /**
   * Number of inherited scale references copied into the duplicate's own folder. A failed
   * copy keeps the source's file, so the duplicate never loses a scale reference.
   */
  copiedScaleReferenceImages?: number
  /** Whether an inherited landing card was copied into the duplicate's own folder. */
  copiedCardImage?: boolean
}

/** DELETE /admin/characters/{character}: the config is gone; file cleanup is best effort. */
export interface DeleteCharacterResponse {
  character: Character
  deletedFiles: number
  failedFiles: number
}

// ----------------------------------------------------------------------------
// IDs
// ----------------------------------------------------------------------------

export const CHARACTER_ID_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/u
export const CHARACTER_ID_MIN_LENGTH = 3
export const CHARACTER_ID_MAX_LENGTH = 40

export function isValidCharacterId(value: unknown): value is Character {
  return (
    typeof value === 'string' &&
    value.length >= CHARACTER_ID_MIN_LENGTH &&
    value.length <= CHARACTER_ID_MAX_LENGTH &&
    CHARACTER_ID_PATTERN.test(value)
  )
}

export function getCharacterIdError(value: string, takenIds: Iterable<string> = []): string | undefined {
  if (!isValidCharacterId(value)) {
    return `Use ${CHARACTER_ID_MIN_LENGTH}–${CHARACTER_ID_MAX_LENGTH} lowercase letters, numbers, and single hyphens (e.g. "buzz-winter").`
  }
  if (getPrincipalCharacterConfig(value)) return 'This ID belongs to a principal character.'
  if (new Set(takenIds).has(value)) return 'This ID is already used, including by archived characters.'
  return undefined
}

function slugify(value: string): string {
  return value
    .normalize('NFKD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/gu, '-')
    .replace(/^-+|-+$/gu, '')
    .slice(0, CHARACTER_ID_MAX_LENGTH)
    .replace(/-+$/u, '')
}

function isTaken(candidate: string, taken: Set<string>): boolean {
  return taken.has(candidate) || Boolean(getPrincipalCharacterConfig(candidate))
}

function withNumberSuffix(base: string, taken: Set<string>): string {
  for (let index = 2; ; index++) {
    const suffix = `-${index}`
    const candidate = `${base.slice(0, CHARACTER_ID_MAX_LENGTH - suffix.length)}${suffix}`
    if (!isTaken(candidate, taken)) return candidate
  }
}

/** Suggests an unused ID from a display name, e.g. "Buzz Winter" → "buzz-winter". */
export function suggestCharacterId(displayName: string, takenIds: Iterable<string> = []): string {
  const base = slugify(displayName)
  if (base.length < CHARACTER_ID_MIN_LENGTH) return ''
  const taken = new Set(takenIds)
  return isTaken(base, taken) ? withNumberSuffix(base, taken) : base
}

/** Suggests the next numbered duplicate, e.g. "doughboy" → "doughboy-4" when 2 and 3 exist. */
export function suggestDuplicateId(sourceId: string, takenIds: Iterable<string> = []): string {
  return withNumberSuffix(sourceId.replace(/-\d+$/u, ''), new Set([...takenIds, sourceId]))
}

// ----------------------------------------------------------------------------
// Landing card images
// ----------------------------------------------------------------------------

export const CHARACTER_CARD_IMAGE_EXTENSIONS = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/webp': 'webp'
} as const

/** Utility-bucket prefix for landing cards. Signed-in users preview them through the image delivery layer. */
export const CHARACTER_CARD_IMAGE_PREFIX = 'character-cards'

/**
 * Versioned so a replaced card is never served from a stale cache:
 * `character-cards/<character>/card-<version>.<ext>`.
 */
export function getCharacterCardImageKey(character: Character, extension: string, version: number): string {
  return `${CHARACTER_CARD_IMAGE_PREFIX}/${character}/card-${version}.${extension}`
}

export function isCharacterCardImageKey(character: Character, key: string): boolean {
  const prefix = `${CHARACTER_CARD_IMAGE_PREFIX}/${character}/`
  return key.startsWith(prefix) && /^card-\d+\.(png|jpg|webp)$/u.test(key.slice(prefix.length))
}

export interface CharacterCardUploadRequest {
  character: Character
  contentType: keyof typeof CHARACTER_CARD_IMAGE_EXTENSIONS
  fileSize: number
}

export interface CharacterCardUploadResponse {
  uploadUrl: string
  key: string
  expiresIn: number
}

/** Generates card candidates with Nano Banana through the async Playground run queue. */
export interface CharacterCardGenerationRequest {
  character: Character
  /** Client-generated UUID; retries with the same ID return the same run. */
  runId: string
  variantCount: 1 | 2 | 3 | 4
  /** Optional scene for the card, e.g. "on a sunny kitchen counter". */
  scene?: string
}

/** Copies a finished candidate into the utility bucket and sets it as the character's card. */
export interface CharacterCardSelectionRequest {
  character: Character
  runId: string
  jobId: string
}

export interface CharacterCardSelectionResponse {
  key: string
}

// ----------------------------------------------------------------------------
// AI prompt helper
// ----------------------------------------------------------------------------

export const CHARACTER_PROMPT_HELPER_STYLES = ['cgi', 'photo', 'illustration'] as const
export type CharacterPromptHelperStyle = (typeof CHARACTER_PROMPT_HELPER_STYLES)[number]

export interface CharacterPromptHelperRequest {
  /** Free-text description of the character's identity and look. */
  description: string
  displayName?: string
  style: CharacterPromptHelperStyle
}

/** The fields the helper drafts; identity and landing fields stay with the producer. */
export type CharacterPromptDraft = Pick<
  DynamicCharacterDefinition,
  'fullName' | 'inputPlaceholder' | 'prompt' | 'background'
>

export interface CharacterPromptHelperResponse {
  draft: CharacterPromptDraft
  /** Validation messages for the drafted fields; the producer fixes them before saving. */
  warnings: string[]
}

// ----------------------------------------------------------------------------
// Config conversion
// ----------------------------------------------------------------------------

/**
 * Selector label. Admins see why a character is restricted; everyone else sees "(unavailable)"
 * for characters they can no longer pick, which only appear in history filters.
 */
export function getCharacterStatusLabel(
  displayName: string,
  status: CharacterStatus,
  visibility: CharacterVisibility = DEFAULT_CHARACTER_VISIBILITY,
  { viewerIsAdmin = false }: { viewerIsAdmin?: boolean } = {}
): string {
  if (status === 'retired') return `${displayName} (retired)`
  if (status === 'draft') return `${displayName} (draft)`
  if (visibility === 'all') return displayName
  if (!viewerIsAdmin) return `${displayName} (unavailable)`
  return visibility === 'hidden' ? `${displayName} (hidden)` : `${displayName} (admin only)`
}

/** Whether a viewer can start new work with a character. History ignores this. */
export function isCharacterSelectable(
  character: Pick<CharacterSummary, 'status' | 'visibility'>,
  { isAdmin }: { isAdmin: boolean }
): boolean {
  if (character.status === 'retired' || character.visibility === 'hidden') return false
  if (character.status === 'draft' || character.visibility === 'admin-only') return isAdmin
  return true
}

/**
 * `create` starts new work (a workflow or a Playground run); `continue` adds a run to an
 * existing workflow, which archived and admin-only characters still allow.
 */
export type CharacterUse = 'create' | 'continue'

/**
 * Whether a viewer may generate with a character. Hidden blocks everyone, admins included;
 * drafts are admin-only test characters until published. The backend enforces this rule and
 * the editor uses it to explain a blocked workflow.
 */
export function canGenerateWithCharacter(
  character: Pick<CharacterSummary, 'status' | 'visibility'>,
  { isAdmin, use }: { isAdmin: boolean; use: CharacterUse }
): boolean {
  if (use === 'create') return isCharacterSelectable(character, { isAdmin })
  if (character.visibility === 'hidden') return false
  return character.status !== 'draft' || isAdmin
}

export function toCharacterConfig(
  id: Character,
  definition: DynamicCharacterDefinition,
  status: CharacterStatus
): CharacterConfig {
  return {
    id,
    displayName: definition.displayName,
    fullName: definition.fullName,
    allowedTools: DYNAMIC_CHARACTER_ALLOWED_TOOLS,
    supportsEC2: false,
    hasScaleReference: Boolean(definition.scaleReferenceKey),
    scaleReferenceKey: definition.scaleReferenceKey,
    prompt: definition.prompt,
    background: definition.background,
    inputPlaceholder: definition.inputPlaceholder,
    landing: definition.landing,
    retired: status === 'retired'
  }
}

/** Prefills an onboarding form from any existing character config. */
export function toDynamicCharacterDefinition(config: CharacterConfig): DynamicCharacterDefinition {
  return {
    displayName: config.displayName,
    fullName: config.fullName,
    prompt: {
      ...config.prompt,
      rules: [...config.prompt.rules],
      examples: config.prompt.examples.map((example) => ({ ...example }))
    },
    background: { ...config.background },
    inputPlaceholder: config.inputPlaceholder,
    landing: { title: config.landing.title, description: config.landing.description, hidden: config.landing.hidden },
    ...(config.hasScaleReference && config.scaleReferenceKey ? { scaleReferenceKey: config.scaleReferenceKey } : {})
  }
}

export function toCharacterSummary(
  config: CharacterConfig,
  source: CharacterSource,
  status: CharacterStatus = config.retired ? 'retired' : 'active',
  {
    duplicatedFrom,
    cardImageKey,
    visibility = DEFAULT_CHARACTER_VISIBILITY,
    viewerIsAdmin = false
  }: {
    duplicatedFrom?: Character
    cardImageKey?: string
    visibility?: CharacterVisibility
    viewerIsAdmin?: boolean
  } = {}
): CharacterSummary {
  return {
    ...(duplicatedFrom ? { duplicatedFrom } : {}),
    ...(cardImageKey ? { cardImageKey } : {}),
    id: config.id,
    displayName: config.displayName,
    label: getCharacterStatusLabel(config.displayName, status, visibility, { viewerIsAdmin }),
    fullName: config.fullName,
    inputPlaceholder: config.inputPlaceholder,
    landing: config.landing,
    allowedTools: config.allowedTools,
    source,
    status,
    visibility
  }
}

export function createEmptyCharacterDefinition(): DynamicCharacterDefinition {
  return {
    displayName: '',
    fullName: '',
    prompt: {
      characterIdentifier: '',
      introDescription: '',
      stylePrefix: 'Soft Focus 3D render, CGI',
      requiredFormat:
        '"Soft Focus 3D render, CGI, {CHARACTER_NAME}, [ACTION FROM USER INPUT], looking at the camera, centered in the frame, full shot, [APPROPRIATE EXPRESSION], on a white background, advertising, soft diffused studio lighting."',
      rules: [
        'ALWAYS keep the character details',
        'ALWAYS end with "on a white background, advertising, soft diffused studio lighting"',
        'Add an appropriate facial expression based on the action',
        'Return ONLY the prompt, no explanations'
      ],
      examples: []
    },
    background: { characterName: '', texture: '', shape: '' },
    inputPlaceholder: '',
    landing: { title: '', description: '' }
  }
}

// ----------------------------------------------------------------------------
// Validation (enrichment slot safety)
// ----------------------------------------------------------------------------

export const CHARACTER_DEFINITION_LIMITS = {
  shortText: 120,
  mediumText: 500,
  longText: 4_000,
  rulesMin: 1,
  rulesMax: 15,
  examplesMax: 5
} as const

/** `{UPPER_SNAKE}` tokens other than {CHARACTER_NAME} are almost always typos. */
const TOKEN_PATTERN = /\{[^{}\n]*\}/gu

export type CharacterDefinitionErrors = Record<string, string>

/**
 * Returns field-path → message for every problem. Keys use dotted paths such as
 * `prompt.rules.2` or `prompt.examples.0.output` so forms can show inline errors.
 */
export function getCharacterDefinitionErrors(
  definition: DynamicCharacterDefinition,
  id: Character = 'preview'
): CharacterDefinitionErrors {
  const errors: CharacterDefinitionErrors = {}
  const { shortText, mediumText, longText } = CHARACTER_DEFINITION_LIMITS

  const checkText = (path: string, value: unknown, max: number, { required = true } = {}) => {
    if (typeof value !== 'string' || (required && value.trim().length === 0)) {
      if (required) errors[path] = 'Required.'
      return
    }
    if (value.length > max) {
      errors[path] = `Keep this to ${max.toLocaleString('en-US')} characters or fewer.`
      return
    }
    if (value.includes(ENRICHMENT_PROMPT_INPUT_MARKER)) {
      errors[path] =
        `${ENRICHMENT_PROMPT_INPUT_MARKER} is reserved for the generation input. Remove it from this field.`
      return
    }
    const unknownTokens = (value.match(TOKEN_PATTERN) ?? []).filter((token) => token !== '{CHARACTER_NAME}')
    if (unknownTokens.length > 0) {
      errors[path] = `Unknown placeholder ${unknownTokens[0]}. Only {CHARACTER_NAME} is supported.`
    }
  }

  checkText('displayName', definition.displayName, shortText)
  checkText('fullName', definition.fullName, shortText)
  checkText('inputPlaceholder', definition.inputPlaceholder, mediumText, { required: false })
  checkText('landing.title', definition.landing?.title, shortText)
  checkText('landing.description', definition.landing?.description, mediumText)

  const prompt = definition.prompt
  checkText('prompt.characterIdentifier', prompt?.characterIdentifier, shortText)
  checkText('prompt.introDescription', prompt?.introDescription, mediumText)
  checkText('prompt.stylePrefix', prompt?.stylePrefix, shortText)
  checkText('prompt.requiredFormat', prompt?.requiredFormat, longText)
  if (!errors['prompt.requiredFormat'] && !prompt.requiredFormat.includes('{CHARACTER_NAME}')) {
    errors['prompt.requiredFormat'] = 'Include {CHARACTER_NAME} where the character name belongs.'
  }

  const rules = Array.isArray(prompt?.rules) ? prompt.rules : []
  if (rules.length < CHARACTER_DEFINITION_LIMITS.rulesMin || rules.length > CHARACTER_DEFINITION_LIMITS.rulesMax) {
    errors['prompt.rules'] =
      `Add between ${CHARACTER_DEFINITION_LIMITS.rulesMin} and ${CHARACTER_DEFINITION_LIMITS.rulesMax} rules.`
  }
  rules.forEach((rule, index) => checkText(`prompt.rules.${index}`, rule, mediumText))

  const examples = Array.isArray(prompt?.examples) ? prompt.examples : []
  if (examples.length > CHARACTER_DEFINITION_LIMITS.examplesMax) {
    errors['prompt.examples'] = `Add at most ${CHARACTER_DEFINITION_LIMITS.examplesMax} examples.`
  }
  examples.forEach((example, index) => {
    checkText(`prompt.examples.${index}.input`, example?.input, mediumText)
    checkText(`prompt.examples.${index}.output`, example?.output, longText)
  })

  checkText('background.characterName', definition.background?.characterName, shortText)
  checkText('background.texture', definition.background?.texture, mediumText)
  checkText('background.shape', definition.background?.shape, mediumText)
  if (
    definition.background?.scaleMode !== undefined &&
    !BACKGROUND_SCALE_MODES.includes(definition.background.scaleMode)
  ) {
    errors['background.scaleMode'] = 'Choose fixed-8-inch or scene-driven.'
  }

  // The rendered code defaults must remain valid enrichment templates.
  if (Object.keys(errors).length === 0) {
    const templates = getCharacterDefinitionTemplates(id, definition)
    const characterError = getEnrichmentPromptTemplateError(templates.character)
    const backgroundError = getEnrichmentPromptTemplateError(templates.background)
    if (characterError) errors['prompt'] = characterError
    if (backgroundError) errors['background'] = backgroundError
  }

  return errors
}

/** Renders the code-default enrichment templates with the [USER_INPUT] slot for previews. */
export function getCharacterDefinitionTemplates(
  id: Character,
  definition: DynamicCharacterDefinition
): { character: string; background: string } {
  const config = toCharacterConfig(id, definition, 'draft')
  return {
    character: getNanoBananaCharacterPrompt(config, ENRICHMENT_PROMPT_INPUT_MARKER),
    background: getBackgroundPrompt(config, ENRICHMENT_PROMPT_INPUT_MARKER)
  }
}
